import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { inject, observer, PropTypes as MobXPropTypes } from 'mobx-react';
import PropTypes from 'prop-types';
import i18n from 'i18n';
import { ReactComponent as PlusIcon } from 'assets/Subtract.svg';
import { USER_CUSTOMER_STATUSES, REGISTRATION_STEPS } from 'components/common/constants';
import { getHeaderButtons } from 'components/common/MenuButtons';
import { PopUpLogOutConfirmationScheme } from 'components/common/PopUpScheme/PopUpLogOutConfirmationScheme';
import { ROUTE_PATHS } from 'routes/constants';
import { getAccountName, getAccountOptions } from './accountNames';
import { useMediaQuery } from 'services/useMediaQUery';
import { MEDIA_QUERY } from 'services/utils';
import { isTokenValid } from 'services/authUtils';
import { IconButton } from 'uikit/IconButton/IconButton';
import { InputDropDown } from 'uikit/InputDropDown/InputDropDown';
import { PopUp } from 'uikit/PopUp/PopUp';

const UserPanel = ({ onClick = () => {}, isMobile = false, userStore, messagesStore, registrationStore }) => {
  const navigate = useNavigate();
  const [showLogOutPopUp, setShowLogOutPopUp] = useState(false);
  const isHeaderButtonNames = useMediaQuery(MEDIA_QUERY.HEADER_BUTTON_NAMES);

  const onHeaderClick = (id, buttonClick) => {
    if (id === 4) {
      return setShowLogOutPopUp(true);
    }
    onClick ? onClick(buttonClick) : buttonClick();
  };

  const getAvailableAccountsOptions = () => getAccountOptions(userStore?.userData?.availableAccounts, i18n);

  const showHeaderDropDown =
    !!userStore?.userData?.availableAccounts?.filter((acc) => acc.status === USER_CUSTOMER_STATUSES.VERIFIED) &&
    userStore.isRepresentativesEnabled();

  const handleChangeAccount = async (name, value) => {
    if (value !== userStore?.userData?.account?.account_number) {
      await userStore.changeCurrentUserAccount(value);
      // TRN-7482: close the mobile navigation menu like any other menu item does — the switch resolves with
      // undefined, so the former `(await ...) && onClick()` never called it
      onClick();
      messagesStore.clearMessagesStore();
      await messagesStore.initializeData(userStore.userData.account.account_number);
    }
  };

  const handleCreateAccount = () => {
    registrationStore.resetRegistrationStore();

    // For logged-in users, set the correct step immediately
    if (isTokenValid()) {
      registrationStore.setCurrentRegistrationStep(REGISTRATION_STEPS.CUSTOMER_CREATION);
      // Set flag to refresh data when returning to dashboard
      sessionStorage.setItem('needsUserDataRefresh', 'true');
      // Save current accounts list for comparison after registration
      const currentAccounts = userStore?.userData?.availableAccounts?.map((acc) => acc.account_number) || [];
      sessionStorage.setItem('accountsBeforeRegistration', JSON.stringify(currentAccounts));
    }

    navigate(ROUTE_PATHS.REGISTRATION_CUSTOMER);
  };

  return (
    <>
      <div className={'account-dropdown-container'}>
        {showHeaderDropDown ? (
          <InputDropDown
            className={'account-dropdown'}
            options={getAvailableAccountsOptions()}
            specificOptions={[
              {
                key: 'open-acc',
                content: (
                  <div className={'open-acc'}>
                    <PlusIcon /> <p>{i18n.getMessage('button.openAnAccount')}</p>
                  </div>
                )
              }
            ]}
            value={userStore?.userData?.account?.account_number}
            onChange={(name, key) => (key === 'open-acc' ? handleCreateAccount() : handleChangeAccount(name, key))}
          />
        ) : (
          <span className={'current-account-name'}>{getAccountName(userStore?.userData?.account, i18n)}</span>
        )}
      </div>
      <div className={`user-panel ${!isHeaderButtonNames && !isMobile ? 'collapsed' : ''}`}>
        {getHeaderButtons(!!userStore.userData.account).map((button) => (
          <IconButton
            key={button.id}
            text={isHeaderButtonNames || isMobile ? button.text : ''}
            onClick={() => onHeaderClick(button.id, button.click)}
            path={button.path}
            navigationType={button.id !== 4}
            Icon={button.icon}
            isDisabled={button.isDisabled}
            unreadTopics={button.path === ROUTE_PATHS.MESSAGES ? messagesStore.unreadTopics : null}
          />
        ))}
        <PopUp alignOnCenter show={showLogOutPopUp}>
          <PopUpLogOutConfirmationScheme onClose={() => setShowLogOutPopUp(false)} />
        </PopUp>
      </div>
    </>
  );
};

UserPanel.propTypes = {
  onClick: PropTypes.any,
  isMobile: PropTypes.bool,
  userStore: MobXPropTypes.observableObject,
  messagesStore: MobXPropTypes.observableObject,
  registrationStore: MobXPropTypes.observableObject
};

export default inject((stores) => ({
  userStore: stores.userStore,
  messagesStore: stores.messagesStore,
  registrationStore: stores.registrationStore
}))(observer(UserPanel));
